import React, { useState, useMemo } from 'react';
import { useBalanceHistory } from '../hooks/useBalances';
import { RefreshCw, Download, ChevronUp, ChevronDown, ArrowUp, ArrowDown } from 'lucide-react';
import './TransferHistory.css';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDateUTC = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, '');
};

function BalanceHistory() {
  const [days, setDays] = useState(30);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [isExpanded, setIsExpanded] = useState(false); // Hidden by default
  const { history, isLoading, isError, error, refresh } = useBalanceHistory(days);

  // Extract all unique exchange and treasury wallet keys from the data
  // Use both locationTotals AND exchangeBalances/treasuryBalances for column detection
  const { exchangeColumns, treasuryColumns } = useMemo(() => {
    const exchanges = new Set();
    const treasuries = new Set();
    
    if (history) {
      history.forEach(snapshot => {
        // Try locationTotals first (preferred, has pre-calculated totals)
        if (snapshot.locationTotals && Object.keys(snapshot.locationTotals).length > 0) {
          Object.keys(snapshot.locationTotals).forEach(key => {
            if (key === 'treasury') {
              treasuries.add('treasury');
            } else if (key.startsWith('treasury_') || key.startsWith('0x')) {
              treasuries.add(key);
            } else {
              exchanges.add(key);
            }
          });
        } else {
          // Fallback: use exchangeBalances and treasuryBalances keys
          if (snapshot.exchangeBalances && Object.keys(snapshot.exchangeBalances).length > 0) {
            Object.keys(snapshot.exchangeBalances).forEach(key => {
              exchanges.add(key);
            });
          }
          if (snapshot.treasuryBalances && Object.keys(snapshot.treasuryBalances).length > 0) {
            treasuries.add('treasury');
          }
        }
      });
    }
    
    return {
      exchangeColumns: Array.from(exchanges).sort(),
      treasuryColumns: Array.from(treasuries).sort(),
    };
  }, [history]);

  // Helper to get location total - from locationTotals or calculated from exchangeBalances
  const getLocationTotal = (snapshot, location) => {
    // First try locationTotals (pre-calculated)
    if (snapshot.locationTotals && snapshot.locationTotals[location]) {
      return parseFloat(snapshot.locationTotals[location]);
    }
    
    // Fallback: calculate from exchangeBalances
    if (location === 'treasury' && snapshot.treasuryBalances) {
      let total = 0;
      Object.values(snapshot.treasuryBalances).forEach(bal => {
        total += parseFloat(bal.available || 0);
      });
      return total;
    }
    
    if (snapshot.exchangeBalances && snapshot.exchangeBalances[location]) {
      let total = 0;
      Object.values(snapshot.exchangeBalances[location]).forEach(bal => {
        // Use equity if available, otherwise available + locked
        if (bal.equity) {
          total += parseFloat(bal.equity);
        } else {
          total += parseFloat(bal.available || 0) + parseFloat(bal.locked || 0);
        }
      });
      return total;
    }
    
    return 0;
  };

  // Sort the data based on current sort configuration
  const sortedHistory = useMemo(() => {
    if (!history || history.length === 0) return [];
    
    const sorted = [...history].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'date':
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;
        case 'totalBalance':
          aValue = a.totalBalanceUsd || 0;
          bValue = b.totalBalanceUsd || 0;
          break;
        case 'pnl':
          aValue = a.totalUnrealizedPnl || 0;
          bValue = b.totalUnrealizedPnl || 0;
          break;
        default:
          // For exchange/treasury columns - use helper function
          aValue = getLocationTotal(a, sortConfig.key);
          bValue = getLocationTotal(b, sortConfig.key);
          break;
      }
      
      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    return sorted;
  }, [history, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span className="sort-icon inactive">⇅</span>;
    }
    return sortConfig.direction === 'desc' 
      ? <ArrowDown size={12} className="sort-icon active" />
      : <ArrowUp size={12} className="sort-icon active" />;
  };

  const handleExportCSV = () => {
    if (!sortedHistory || sortedHistory.length === 0) return;

    // Build columns dynamically
    const columns = ['Date (UTC)', 'Total Balance (USD)', 'Unrealized P&L (USD)'];
    
    // Add treasury columns
    treasuryColumns.forEach(col => {
      const label = col === 'treasury' ? 'Treasury' : col;
      columns.push(`${label} (USD)`);
    });
    
    // Add exchange columns
    exchangeColumns.forEach(ex => {
      columns.push(`${ex.charAt(0).toUpperCase() + ex.slice(1)} (USD)`);
    });

    // Create CSV content
    let csvContent = columns.join(',') + '\n';

    sortedHistory.forEach(snapshot => {
      const row = [
        formatDateUTC(snapshot.date),
        snapshot.totalBalanceUsd || 0,
        snapshot.totalUnrealizedPnl || 0,
      ];
      
      treasuryColumns.forEach(col => {
        row.push(snapshot.locationTotals?.[col] || 0);
      });

      exchangeColumns.forEach(ex => {
        row.push(snapshot.locationTotals?.[ex] || 0);
      });

      csvContent += row.join(',') + '\n';
    });

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `balance_history_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatColumnLabel = (key) => {
    if (key === 'treasury') return 'Treasury';
    if (key.startsWith('0x')) return `${key.slice(0, 6)}...${key.slice(-4)}`;
    return key.charAt(0).toUpperCase() + key.slice(1);
  };

  return (
    <div className="transfer-history">
      <div className="history-header">
        <div className="header-left">
          <h3>Balance History</h3>
          <button 
            onClick={() => setIsExpanded(!isExpanded)} 
            className="btn-toggle"
            title={isExpanded ? 'Hide table' : 'Show table'}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            <span>{isExpanded ? 'Hide' : 'Show'}</span>
          </button>
          {isExpanded && (
            <select 
              value={days} 
              onChange={(e) => setDays(Number(e.target.value))}
              className="days-select"
            >
              <option value={7}>Last 7 Days</option>
              <option value={30}>Last 30 Days</option>
              <option value={90}>Last 90 Days</option>
            </select>
          )}
        </div>
        {isExpanded && (
          <div className="header-actions">
            <button onClick={handleExportCSV} className="btn-export" disabled={!history?.length}>
              <Download size={14} />
              <span>Export CSV</span>
            </button>
            <button onClick={refresh} className="btn-refresh" disabled={isLoading}>
              <RefreshCw size={14} className={isLoading ? 'spinning' : ''} />
            </button>
          </div>
        )}
      </div>

      {isExpanded && (
        <>
          {isError && (
            <div className="history-error">
              <p>Failed to load balance history: {error?.message}</p>
              <button onClick={refresh}>Retry</button>
            </div>
          )}

          {isLoading && (!history || history.length === 0) && (
            <div className="history-loading">
              <div className="loading-spinner"></div>
              <span>Loading history...</span>
            </div>
          )}

          {!isError && sortedHistory && sortedHistory.length > 0 && (
            <div className="history-table-wrapper balance-history-table">
              <table className="history-table sortable-table">
                <thead>
                  <tr>
                    <th 
                      className="sortable-header" 
                      onClick={() => handleSort('date')}
                    >
                      <span>Date (UTC)</span>
                      <SortIcon columnKey="date" />
                    </th>
                    <th 
                      className="sortable-header mono text-right" 
                      onClick={() => handleSort('totalBalance')}
                    >
                      <span>Total Balance</span>
                      <SortIcon columnKey="totalBalance" />
                    </th>
                    <th 
                      className="sortable-header mono text-right" 
                      onClick={() => handleSort('pnl')}
                    >
                      <span>P&L</span>
                      <SortIcon columnKey="pnl" />
                    </th>
                    {treasuryColumns.map(col => (
                      <th 
                        key={col}
                        className="sortable-header mono text-right" 
                        onClick={() => handleSort(col)}
                      >
                        <span>{formatColumnLabel(col)}</span>
                        <SortIcon columnKey={col} />
                      </th>
                    ))}
                    {exchangeColumns.map(ex => (
                      <th 
                        key={ex}
                        className="sortable-header mono text-right" 
                        onClick={() => handleSort(ex)}
                      >
                        <span>{formatColumnLabel(ex)}</span>
                        <SortIcon columnKey={ex} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                    {sortedHistory.map((snapshot) => (
                    <tr key={snapshot.date} className="data-row">
                      <td className="date-cell mono">{formatDateUTC(snapshot.date)}</td>
                      <td className="mono text-right font-bold">
                        {formatCurrency(snapshot.totalBalanceUsd)}
                      </td>
                      <td className={`mono text-right ${(snapshot.totalUnrealizedPnl || 0) >= 0 ? 'text-green' : 'text-red'}`}>
                        {formatCurrency(snapshot.totalUnrealizedPnl)}
                      </td>
                      {treasuryColumns.map(col => (
                        <td key={col} className="mono text-right">
                          {formatCurrency(getLocationTotal(snapshot, col))}
                        </td>
                      ))}
                      {exchangeColumns.map(ex => (
                        <td key={ex} className="mono text-right">
                          {formatCurrency(getLocationTotal(snapshot, ex))}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isLoading && !isError && (!history || history.length === 0) && (
            <div className="history-empty">
              <p>No history found</p>
            </div>
          )}
        </>
      )}

      {!isExpanded && (
        <div className="history-collapsed-notice">
          <p>Balance history is hidden. Click "Show" to view the table.</p>
        </div>
      )}
    </div>
  );
}

export default BalanceHistory;
